import { useEffect, useRef } from 'react'

/**
 * Nghe máy scan mã vạch / mã QR.
 *
 * Gần như toàn bộ máy scan cầm tay bán ở Việt Nam là loại USB giả bàn phím: quét xong
 * nó "gõ" từng ký tự của mã rồi kết thúc bằng Enter. Không cần driver, không cần thư
 * viện, và chạy được ngay trong trình duyệt thường.
 *
 * Việc duy nhất phải làm là <b>phân biệt máy scan với người gõ tay</b>. Máy scan bắn
 * cả chuỗi trong khoảng 20–50 mili giây mỗi ký tự; người nhanh nhất cũng không dưới
 * 80ms. Chuỗi nào đến nhanh hơn ngưỡng và kết thúc bằng Enter thì coi là mã quét.
 */

const MAX_GAP_MS = 60
const MIN_LENGTH = 4

interface Options {
  onScan: (code: string) => void
  enabled?: boolean
}

export function useBarcodeScanner({ onScan, enabled = true }: Options): void {
  const buffer = useRef('')
  const lastKeyAt = useRef(0)
  const handler = useRef(onScan)

  // Giữ hàm xử lý mới nhất mà không gắn/gỡ lại listener mỗi lần vẽ lại.
  handler.current = onScan

  useEffect(() => {
    if (!enabled) return

    function onKeyDown(e: KeyboardEvent) {
      // Người dùng đang gõ vào ô nhập thì để yên — họ có thể đang tìm món bằng tay,
      // hoặc chủ ý quét mã vào đúng ô "mã vạch" ở màn hình sửa sản phẩm.
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return

      const now = performance.now()
      const gap = now - lastKeyAt.current
      lastKeyAt.current = now

      // Ngắt quãng lâu nghĩa là chuỗi trước đã đứt — bắt đầu chuỗi mới.
      if (gap > MAX_GAP_MS) buffer.current = ''

      if (e.key === 'Enter') {
        const code = buffer.current.trim()
        buffer.current = ''
        if (code.length >= MIN_LENGTH) {
          e.preventDefault()
          handler.current(code)
        }
        return
      }

      // Máy scan chỉ bắn ký tự thường. Bỏ qua Shift, Ctrl, F1…
      if (e.key.length === 1) buffer.current += e.key
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}

/**
 * Đọc mã vào một ô nhập cụ thể — dùng ở màn hình gán mã vạch cho sản phẩm.
 * Ở đây không cần đo tốc độ gõ: người dùng đã chủ ý đặt con trỏ vào ô này.
 */
export function scannerInputProps(onScan: (code: string) => void) {
  return {
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return
      e.preventDefault()
      const code = e.currentTarget.value.trim()
      if (code) onScan(code)
    },
  }
}
